/**
 * Decision 0067 (TRI-01): the runs a pull request still needs, kept by snapshot compaction and restored into a state
 * that an older compaction already stripped.
 *
 * Every PR read derives from the run that posted the PR artifact (`artifact.runId`): whether it is the item's own PR
 * (`isOwnPr`: a worker run of the item whose branch is the PR's head), whose gh merges it (`prOwnerOf`), which runner
 * and repo watch it (`prRoutingOf`), and the SHA a Merge card is bound to (`reviewedShaOf`: the newest of the item's
 * worker runs that posted or re-posted the PR, `postedRunsOf`). So compaction keeps those runs instead of the PR
 * copying them.
 *
 * No runtime imports from the rest of core (`./state` imports this module for `compact`, `./github` for the helpers).
 */
import { parseGithubPull } from "@minga/protocol"
import type { Artifact, Item, ProjectState, Run, RunId } from "./types"

/** A cancelled item's open PR is watched this long after the item closed (decision 0059 §3), then dropped. */
export const PR_WATCH_CANCELLED_KEEP_MS = 7 * 86_400_000

/** The head SHA of a run's pull_request evidence (the accepted outcome first, then the claim); moved from the cloud. */
export const pullRequestHeadSha = (run: Run | null | undefined, url?: string): string | null => {
  const want = url !== undefined ? parseGithubPull(url)?.url : undefined
  for (const o of [run?.outcome, run?.claimed]) {
    if (o?._tag !== "done") continue
    for (const e of o.evidence) {
      if (e._tag !== "pull_request" || !/^[0-9a-f]{40}$/.test(e.headSha)) continue
      if (want !== undefined && parseGithubPull(e.url)?.url !== want) continue
      return e.headSha
    }
  }
  return null
}

/**
 * The item's worker runs that posted the PR (`artifact.runId`) or re-posted it (artifacts dedupe on item, kind and uri:
 * the run's done evidence names the URL), newest first (by end, else request). `reviewedShaOf` binds Merge to the first
 * of them with a remote head, else the first with evidence for this URL.
 */
export const postedRunsOf = (s: ProjectState, a: Artifact): ReadonlyArray<Run> => {
  const url = parseGithubPull(a.uri)?.url
  return s.runs
    .filter(
      (r) =>
        r.kind === "worker" &&
        r.itemId === a.itemId &&
        (r.id === a.runId || (url !== undefined && pullRequestHeadSha(r, url) !== null)),
    )
    .sort((x, y) => (y.endedAt ?? y.requestedAt) - (x.endedAt ?? x.requestedAt))
}

/**
 * A PR artifact that still matters: a github.com pull request (nothing else can be the item's own PR or be watched)
 * that is not rejected (a closed PR is rejected), and either its item is still open (a merge or a close on GitHub can
 * still move it, a card can still merge it), or the PR is still watched: open, or never observed while its item is
 * not a cancelled one past the watch window (`allWatchEntries`).
 */
const needsRuns = (a: Artifact, t: Item | undefined, now: number): boolean => {
  if (a.kind !== "pull_request" || a.runId === null || a.state === "rejected") return false
  if (!parseGithubPull(a.uri)) return false
  if (t !== undefined && t.status !== "done" && t.status !== "cancelled") return true
  if (a.pr !== undefined) return a.pr.snapshot.state === "open"
  return !(t?.status === "cancelled" && t.closedAt !== null && t.closedAt < now - PR_WATCH_CANCELLED_KEEP_MS)
}

/**
 * The runs compaction must keep for the PRs that still matter at `now` (decision 0067): each such PR's posting run
 * (ownership, owner, routing), its item's last worker attempt (`lastRunId`), and the runs `reviewedShaOf` would bind
 * Merge to (the newest posted run with a remote head, and the newest with evidence for this URL). At most four runs
 * per PR, usually one or two.
 */
export const prRunIdsOf = (s: ProjectState, now: number): ReadonlySet<RunId> => {
  const out = new Set<RunId>()
  for (const a of s.artifacts) {
    const t = a.itemId === null ? undefined : s.items.find((x) => x.id === a.itemId)
    if (!needsRuns(a, t, now) || a.runId === null) continue
    out.add(a.runId)
    if (t?.lastRunId) out.add(t.lastRunId)
    const url = parseGithubPull(a.uri)?.url
    const posted = postedRunsOf(s, a)
    const withHead = posted.find((r) => r.head?.remoteSha)
    if (withHead) out.add(withHead.id)
    const withEvidence = posted.find((r) => pullRequestHeadSha(r, url) !== null)
    if (withEvidence) out.add(withEvidence.id)
  }
  return out
}

/**
 * The runs `prRunIdsOf` names that the state no longer holds (an older snapshot compacted them away). Only the posting
 * run and `lastRunId` can be named while missing: the binding runs are found among the runs the state holds.
 */
export const missingPrRunIds = (s: ProjectState, now: number): ReadonlyArray<RunId> => {
  const have = new Set(s.runs.map((r) => r.id))
  return [...prRunIdsOf(s, now)].filter((id) => !have.has(id))
}

/**
 * The state with `runs` put back (decision 0067: the repair of a state compacted before the fix). Only runs it lacks
 * and `prRunIdsOf` names are added, in `requestedAt` order; the records are the ones the log produced (the `runs`
 * projection or a fold), never made up. Same state back when nothing is added.
 */
export const restoreRuns = (s: ProjectState, runs: ReadonlyArray<Run>, now: number): ProjectState => {
  const want = prRunIdsOf(s, now)
  const have = new Set(s.runs.map((r) => r.id))
  const add = runs.filter(
    (r, i) => want.has(r.id) && !have.has(r.id) && runs.findIndex((x) => x.id === r.id) === i,
  )
  if (add.length === 0) return s
  // each one goes before the first held run requested after it: the held runs keep their order (the log's)
  const runsOut = [...s.runs]
  for (const r of [...add].sort((x, y) => x.requestedAt - y.requestedAt)) {
    const at = runsOut.findIndex((x) => x.requestedAt > r.requestedAt)
    runsOut.splice(at === -1 ? runsOut.length : at, 0, r)
  }
  return { ...s, runs: runsOut }
}

/**
 * The items whose PR got a run back (`restored`): the review cards to recompute after a repair (decision 0067). The
 * items of the PRs that still matter whose posting run or last attempt is among `restored`.
 */
export const itemsOfRestoredRuns = (
  s: ProjectState,
  restored: ReadonlyArray<RunId>,
  now: number,
): ReadonlyArray<Item["id"]> => {
  const back = new Set(restored)
  const out = new Set<Item["id"]>()
  for (const a of s.artifacts) {
    if (a.itemId === null) continue
    const t = s.items.find((x) => x.id === a.itemId)
    if (!needsRuns(a, t, now)) continue
    if ((a.runId !== null && back.has(a.runId)) || (t?.lastRunId && back.has(t.lastRunId))) out.add(a.itemId)
  }
  return [...out]
}
